import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Plus, Trash2, Calculator, Save } from 'lucide-react';
import { estimatesApi, locationsApi, roomTypesApi } from '@/api/client';
import { formatCurrency } from '@medical-counsellor/shared';
import { toast } from '@/utils/toast';

const customEstimateSchema = z.object({
    patientName: z.string().min(1, 'Patient name is required'),
    patientId: z.string().optional(),
    locationId: z.string().min(1, 'Location is required'),
    roomType: z.string().min(1, 'Room type is required'),
    stayDays: z.number().int().positive('Stay days must be positive'),
    procedureName: z.string().min(1, 'Procedure name is required'),
    items: z.array(z.object({
        name: z.string().min(1, 'Item name is required'),
        quantity: z.number().positive('Quantity must be positive'),
        unitPrice: z.number().nonnegative('Unit price must be non-negative'),
    })).min(1, 'At least one item is required'),
});

type CustomEstimateFormData = z.infer<typeof customEstimateSchema>;

export function NewCustomEstimatePage() {
    const navigate = useNavigate();

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<CustomEstimateFormData>({
        resolver: zodResolver(customEstimateSchema),
        defaultValues: {
            patientName: '',
            patientId: '',
            locationId: '',
            roomType: '',
            stayDays: 1,
            procedureName: '',
            items: [{ name: '', quantity: 1, unitPrice: 0 }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'items',
    });

    const selectedLocationId = watch('locationId');
    const selectedRoomType = watch('roomType');
    const stayDays = watch('stayDays');
    const items = watch('items');

    const { data: roomTypes = [] } = useQuery({
        queryKey: ['room-types', selectedLocationId],
        queryFn: () => roomTypesApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const createMutation = useMutation({
        mutationFn: estimatesApi.createCustom,
        onSuccess: (data) => {
            toast.success('Custom estimate created successfully');
            navigate(`/estimates/${data.id}`);
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create estimate');
        },
    });

    const onSubmit = (data: CustomEstimateFormData) => {
        createMutation.mutate(data);
    };

    // Calculate preview
    const selectedRoom = roomTypes.find((r: any) => r.name === selectedRoomType);
    const roomTotal = selectedRoom ? selectedRoom.defaultRate * stayDays : 0;
    const markupPercent = selectedRoom?.markupPercentage || 0;
    const markupMultiplier = 1 + (markupPercent / 100);

    let itemsSubtotal = 0;
    let itemsTotal = 0;
    items.forEach((item) => {
        const base = (item.unitPrice || 0) * (item.quantity || 0);
        itemsSubtotal += base;
        itemsTotal += base * markupMultiplier;
    });

    const total = roomTotal + itemsTotal;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/estimates')}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Create Custom Estimate</h1>
                    <p className="text-slate-600">Manual entry for procedures not in the system</p>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Form */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Patient Information */}
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-lg font-semibold">Patient Information</h2>
                            </div>
                            <div className="card-body space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">Patient Name *</label>
                                        <input
                                            type="text"
                                            className={`input ${errors.patientName ? 'input-error' : ''}`}
                                            placeholder="Enter patient name"
                                            {...register('patientName')}
                                        />
                                        {errors.patientName && (
                                            <p className="error-text">{errors.patientName.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="label">Patient ID (Optional)</label>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="Enter patient ID"
                                            {...register('patientId')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="label">Procedure Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.procedureName ? 'input-error' : ''}`}
                                        placeholder="e.g., Emergency Surgery"
                                        {...register('procedureName')}
                                    />
                                    {errors.procedureName && (
                                        <p className="error-text">{errors.procedureName.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Room & Stay Details */}
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-lg font-semibold">Room & Stay Details</h2>
                            </div>
                            <div className="card-body space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="label">Location *</label>
                                        <select
                                            className={`input ${errors.locationId ? 'input-error' : ''}`}
                                            {...register('locationId')}
                                        >
                                            <option value="">Select location...</option>
                                            {locations.map((location: any) => (
                                                <option key={location.id} value={location.id}>
                                                    {location.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.locationId && (
                                            <p className="error-text">{errors.locationId.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="label">Room Type *</label>
                                        <select
                                            className={`input ${errors.roomType ? 'input-error' : ''}`}
                                            {...register('roomType')}
                                            disabled={!selectedLocationId}
                                        >
                                            <option value="">Select room type...</option>
                                            {roomTypes.map((room: any) => (
                                                <option key={room.id} value={room.name}>
                                                    {room.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.roomType && (
                                            <p className="error-text">{errors.roomType.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="label">Stay Days *</label>
                                        <input
                                            type="number"
                                            min="1"
                                            className={`input ${errors.stayDays ? 'input-error' : ''}`}
                                            {...register('stayDays', { valueAsNumber: true })}
                                        />
                                        {errors.stayDays && (
                                            <p className="error-text">{errors.stayDays.message}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div className="card">
                            <div className="card-header flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Cost Items</h2>
                                <button
                                    type="button"
                                    onClick={() => append({ name: '', quantity: 1, unitPrice: 0 })}
                                    className="btn btn-sm btn-secondary"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Item
                                </button>
                            </div>
                            <div className="card-body">
                                <div className="space-y-3">
                                    {fields.map((field, index) => (
                                        <div
                                            key={field.id}
                                            className="grid grid-cols-12 gap-3 items-start p-3 bg-slate-50 rounded-lg border border-slate-200"
                                        >
                                            <div className="col-span-5">
                                                <label className="text-xs text-slate-500 mb-1 block">Item Name</label>
                                                <input
                                                    type="text"
                                                    className={`input input-sm ${errors.items?.[index]?.name ? 'input-error' : ''}`}
                                                    placeholder="e.g., Surgery, Medicine"
                                                    {...register(`items.${index}.name`)}
                                                />
                                                {errors.items?.[index]?.name && (
                                                    <p className="text-xs text-error-600 mt-1">
                                                        {errors.items[index]?.name?.message}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="col-span-2">
                                                <label className="text-xs text-slate-500 mb-1 block">Quantity</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className={`input input-sm ${errors.items?.[index]?.quantity ? 'input-error' : ''}`}
                                                    {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                                                />
                                            </div>

                                            <div className="col-span-3">
                                                <label className="text-xs text-slate-500 mb-1 block">Unit Price</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className={`input input-sm ${errors.items?.[index]?.unitPrice ? 'input-error' : ''}`}
                                                    placeholder="0.00"
                                                    {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                                                />
                                            </div>

                                            <div className="col-span-2 flex items-end justify-end">
                                                {fields.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => remove(index)}
                                                        className="btn btn-sm btn-error"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {errors.items?.root && (
                                    <p className="error-text mt-2">{errors.items.root.message}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Preview Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="card sticky top-6">
                            <div className="card-header flex items-center gap-2">
                                <Calculator className="w-5 h-5 text-primary-600" />
                                <h2 className="text-lg font-semibold">Cost Preview</h2>
                            </div>

                            <div className="card-body space-y-4">
                                {selectedRoom ? (
                                    <>
                                        {/* Room Charges */}
                                        <div>
                                            <h3 className="text-sm font-medium text-slate-700 mb-2">Room Charges</h3>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-600">
                                                    {selectedRoom.name} × {stayDays} days
                                                </span>
                                                <span className="font-medium">{formatCurrency(roomTotal)}</span>
                                            </div>
                                        </div>

                                        {/* Items */}
                                        {items.length > 0 && items.some(i => i.name) && (
                                            <div className="border-t pt-4">
                                                <h3 className="text-sm font-medium text-slate-700 mb-2">Other Items</h3>
                                                <div className="space-y-1">
                                                    {items.map((item, index) => {
                                                        if (!item.name) return null;
                                                        const base = (item.unitPrice || 0) * (item.quantity || 0);
                                                        const withMarkup = base * markupMultiplier;
                                                        return (
                                                            <div key={index} className="flex justify-between text-sm">
                                                                <span className="text-slate-600 truncate">
                                                                    {item.name} × {item.quantity || 0}
                                                                </span>
                                                                <span className="font-medium ml-2">
                                                                    {formatCurrency(withMarkup)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Total */}
                                        <div className="border-t-2 pt-4">
                                            <div className="flex justify-between items-center">
                                                <span className="font-semibold text-slate-900">Total Estimate</span>
                                                <span className="text-xl font-bold text-primary-600">
                                                    {formatCurrency(total)}
                                                </span>
                                            </div>
                                            {markupPercent > 0 && (
                                                <p className="text-xs text-amber-600 mt-2">
                                                    * Includes {markupPercent}% room type premium (excludes room charges)
                                                </p>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-slate-500">
                                        <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm">Select location and room to see preview</p>
                                    </div>
                                )}
                            </div>

                            <div className="card-footer">
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-md w-full"
                                    disabled={createMutation.isPending}
                                >
                                    <Save className="w-4 h-4" />
                                    {createMutation.isPending ? 'Creating...' : 'Generate Estimate'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
